import { useCallback, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Switch,
  Grid,
} from "@mui/material";
import apiService from "../../../service/Api"; // ✅ API service
import type { IThemeState } from "../../../types/theme";


interface GalleryItem {
  id: number;
  file: string; // API returns file URL
  title?: string;
  content_type?: string;
}

interface GalleryPanelProps {
  reduxTheme: IThemeState;
  deceasedId: number | undefined;
}

const GalleryPanel = ({ reduxTheme, deceasedId }: GalleryPanelProps) => {
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchGallery = useCallback(async () => {
    if (!deceasedId) {
      console.warn("GalleryPanel: deceasedId is missing");
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.get<{ results: GalleryItem[] }>(
        `/api/individuals/deceased-gallery/?deceased_id=${deceasedId}`
      );

      setGallery(response.data.results || []);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  }, [deceasedId]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  return (
    <Card
      sx={{
        height: 482,
        backgroundColor: reduxTheme.primary_colors?.[800],
        width: 660,
        pt: 6,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2, height: "100%", overflow: "hidden" }}>
        {/* HEADER */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%", mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Choose pictures to display
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Enable/Disable
            </Typography>
            <Switch size="small" defaultChecked />
          </Box>
        </Box>

        {/* LOADING */}
        {loading && (
          <Typography variant="body1" sx={{ opacity: 0.7 }}>
            Loading gallery...
          </Typography>
        )}

        {/* EMPTY */}
        {!loading && gallery.length === 0 && (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No gallery images found.
          </Typography>
        )}

        {/* GALLERY GRID */}
        <Grid container spacing={2}>
          {!loading &&
            gallery.map((item) => (
              <Grid size={{ xs: 3 }} key={item.id}>
                <Paper
                  sx={{
                    height: 150, // keeps your original height
                    width: "100%", // make sure Paper fills Grid cell
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative", // for optional title overlay
                  }}
                >
                  <Box
                    component="img"
                    src={item.file}
                    alt={item.title || "Gallery"}
                    sx={{
                      width: "100%", // fill Paper width
                      height: "100%", // fill Paper height
                      objectFit: "cover", // cover the box without distortion
                      display: "block",
                    }}
                  />
                </Paper>
              </Grid>
            ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default GalleryPanel;
